import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  FileButton,
  Grid,
  Group,
  Loader,
  Stack,
  Stepper,
  Text,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCheck, IconDownload, IconUpload } from "@tabler/icons";
import { useServerApi } from "../../hooks/userServerApi";
import { downloadEntityTemplate } from "../../helpers/migrationTemplateDownload";
import {
  formatImportSummary,
  getImportErrorMessage,
  hasImportWarnings,
} from "../../helpers/importResultUtils";
import MigrationBackLink from "./MigrationBackLink";

const CUSTOM_STEPS = [
  {
    id: "productType",
    label: "Insurance Classes",
    description: "Import product types (classes)",
    apiEntity: "productType",
    templateFileName: "product-type-template",
  },
  {
    id: "provider",
    label: "Insurers",
    description: "Import providers (insurers)",
    apiEntity: "provider",
    templateFileName: "provider-template",
  },
  {
    id: "user",
    label: "Agents / Producers",
    description: "Import consultant users",
    apiEntity: "user",
    templateFileName: "user-template",
  },
  {
    id: "client",
    label: "Clients",
    description: "Import clients",
    apiEntity: "client",
    templateFileName: "client-template",
  },
  {
    id: "debitNote",
    label: "Policies (Debit Notes)",
    description: "Import GI policies as debit notes",
    apiEntity: "debitNote",
    templateFileName: "debit-note-template",
  },
];

const CustomDataMigrationWizard = () => {
  const [api] = useServerApi();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [file, setFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentStep = CUSTOM_STEPS[activeStep];

  const resetStepState = () => {
    setFile(null);
    setImportResult(null);
  };

  const handleStepSelect = (index) => {
    setActiveStep(index);
    resetStepState();
  };

  const downloadTemplate = async () => {
    if (!currentStep) return;
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_SERVER_URL}/${currentStep.apiEntity}/downloadTemplate`;
      await downloadEntityTemplate(url, currentStep.templateFileName);
      showNotification({
        title: "Template downloaded",
        message: "MBID import template downloaded",
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: "Download failed",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const runImport = async () => {
    if (!file || !currentStep) return;
    setLoading(true);
    try {
      const result = await api.importBatch({
        apiEntity: currentStep.apiEntity,
        file,
      });
      setImportResult(result.data);
      setCompletedSteps((prev) => [...new Set([...prev, activeStep])]);
      showNotification({
        color: hasImportWarnings(result.data) ? "yellow" : "green",
        title: hasImportWarnings(result.data) ? "Import completed with warnings" : "Import completed",
        message: formatImportSummary(result.data),
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: "Import failed",
        message: getImportErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing="md">
      <MigrationBackLink />
      <Title order={3}>Custom Data Migration</Title>
      <Text size="sm" color="dimmed">
        Download MBID import templates, fill in your data, then import each
        entity in dependency order.
      </Text>

      <Grid gutter="lg">
        <Grid.Col span={12} md={3}>
          <Stepper
            active={activeStep}
            onStepClick={handleStepSelect}
            orientation="vertical"
            breakpoint="sm"
          >
            {CUSTOM_STEPS.map((step, index) => (
              <Stepper.Step
                key={step.id}
                label={step.label}
                description={step.apiEntity}
                icon={
                  completedSteps.includes(index) ? (
                    <IconCheck size={16} />
                  ) : (
                    index + 1
                  )
                }
              />
            ))}
          </Stepper>
        </Grid.Col>

        <Grid.Col span={12} md={9}>
          {currentStep ? (
            <Card withBorder padding="lg">
              <Stack spacing="sm">
                <Group>
                  <Title order={4}>{currentStep.label}</Title>
                  <Badge>{currentStep.apiEntity}</Badge>
                </Group>
                <Text size="sm">{currentStep.description}</Text>

                <Group>
                  <Button
                    disabled={loading}
                    color="blue"
                    leftIcon={<IconDownload size={16} />}
                    onClick={downloadTemplate}
                  >
                    Download MBID template
                  </Button>
                  <FileButton onChange={setFile} accept=".xls,.xlsx">
                    {(props) => (
                      <Button
                        {...props}
                        leftIcon={<IconUpload size={16} />}
                      >
                        {file ? file.name : "Upload filled template"}
                      </Button>
                    )}
                  </FileButton>
                  <Button
                    disabled={!file || loading}
                    color="green"
                    onClick={runImport}
                  >
                    Import this step
                  </Button>
                </Group>

                {loading && <Loader size="sm" />}

                {importResult && (
                  <Alert
                    color={hasImportWarnings(importResult) ? "yellow" : "green"}
                    title="Import result"
                  >
                    {formatImportSummary(importResult)}
                  </Alert>
                )}

              </Stack>
            </Card>
          ) : null}
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default CustomDataMigrationWizard;
